import { useMyUser } from '@consigli/hooks';
import { logout } from '@consigli/oauth';
import { ProjectRole } from '@consigli/types';
import i18n from 'i18next';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { useDropdown } from '@/layouts/page-header/use-dropdown';
import { fetchMessageTemplates } from '@/locales/message-templates';
import { Route } from '@/routes';
import { formatDisplayName } from '@/util/format-name';
import { DropdownElement } from '@/util/interfaces';
import { authConfig } from '@/util/oauth/config';

import { PageHeaderDropdown } from './page-header-dropdown';

export const UserMenuButton = () => {
  const { t } = useTranslation();
  const { user } = useMyUser();
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { isOpen, toggle, open, close, handleKeyDown } = useDropdown(dropdownRef);

  const hasUserManagementAccess: boolean = useMemo(
    () =>
      user.isSuperuser ||
      user.isOrgAdmin ||
      user.projectAccess.some((project) => project.role === ProjectRole.ADMIN),
    [user.isOrgAdmin, user.projectAccess, user.isSuperuser],
  );

  // UseEffect to fetch message templates for translation
  useEffect(() => {
    if (user) {
      const currentLanguage = i18n.language;
      fetchMessageTemplates(currentLanguage, 'translation');
    }
  }, [user]);

  const profileOptions: DropdownElement[] = [
    {
      text: t('navbar.menu.myprofile'),
      onClick: () => navigate('/profile'),
    },
    ...(hasUserManagementAccess
      ? [
          {
            text: t('navbar.menu.user-and-project-management'),
            onClick: () => navigate(Route.USER_MANAGEMENT),
          },
        ]
      : []),
    {
      text: t('navbar.menu.logout'),
      onClick: () => logout(authConfig),
    },
  ];

  return (
    <div className="relative" ref={dropdownRef} onMouseLeave={close}>
      <button
        className="py-1.5 px-2 cursor-pointer hover:bg-day-light-3 rounded-full relative"
        onClick={toggle}
        onMouseEnter={open}
        onKeyDown={handleKeyDown}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <i className="fas fa-user-circle fa-xl text-day-neutral-dark" aria-hidden="true" />
        <span className="sr-only">User menu</span>
      </button>
      <PageHeaderDropdown
        header={formatDisplayName(user.firstName, user.lastName)}
        elements={profileOptions}
        className={`right-0 ${isOpen ? 'block' : 'hidden'} min-w-[200px] z-50`}
      />
    </div>
  );
};
